import { IS_BLOCK_EDITOR_MARKDOWN_ENABLED } from '@/components/constants/flagsWithUserQueryDep';
import { reportSectionMarkerResidue } from '@/components/tiptap/shared/section-residue';
import { reformatHtml } from '@/helpers/markdownToHtml';
import { ClipboardItem } from 'clipboard-polyfill';
import { Converter } from 'showdown';
import { simpleHtmlToMarkdown } from '../htmlToMarkdown';
import {
  serializedClipboardWrite,
  writeHtmlAndMarkdownToClipboard,
} from './copyHelpers-v2';
import { resolveClipboardPlainText } from './plainTextClipboard';

export const oldCopyHtmlWithoutLists = async (copyContent: string) => {
  const converter = new Converter();
  converter.setOption('simpleLineBreaks', true);
  //     Showdown allows you to use backslash (\) escapes to generate literal characters which would otherwise have special meaning in markdown's syntax. For example, if you wanted to surround a word with literal underscores (instead of an HTML <em> tag), you can use backslashes before the unserscores, like this:
  // \_literal underscores\_

  const escapeMarkdownListsContent = copyContent
    // Escape unordered lists add \ to the beginning of each list item
    ?.replace(/^- /gm, '\\- ')
    // Escape ordered lists add \ to the end of each number
    ?.replace(/(\d+)\. /g, '$1\\. ');

  // make new lines markdown friendly
  const newLineContent = escapeMarkdownListsContent?.replace(/\n\n/g, '<br/><br/>');
  copyContent = copyContent.replace(/</g, '&lt;');
  const htmlContent = converter.makeHtml(newLineContent || '');
  const markdown = copyContent.replace(/&lt;/g, '<');
  const plainText = resolveClipboardPlainText({ markdown });
  reportSectionMarkerResidue({
    payloads: [
      { flavor: 'html', value: htmlContent },
      { flavor: 'text', value: plainText },
    ],
    surface: 'copy',
  });
  const item = new ClipboardItem({
    'text/html': new Blob([htmlContent], {
      type: 'text/html',
    }),
    'text/plain': new Blob([plainText], {
      type: 'text/plain',
    }),
  });

  await serializedClipboardWrite([item]);
};

export const oldCopyFullHtml = async ({
  html,
  markdown,
}:
  | {
      html: string;
      markdown?: string;
    }
  | {
      html?: string;
      markdown: string;
    }) => {
  if (IS_BLOCK_EDITOR_MARKDOWN_ENABLED()) {
    const reformattedHtml = reformatHtml(html ?? '');
    const markdown = simpleHtmlToMarkdown({
      html: html ?? '',
      isCopy: true,
    });
    const plainText = resolveClipboardPlainText({
      markdown: markdown.replace(/&lt;/g, '<'),
    });

    reportSectionMarkerResidue({
      payloads: [
        { flavor: 'html', value: reformattedHtml },
        { flavor: 'text', value: plainText },
      ],
      surface: 'copy',
    });
    await serializedClipboardWrite([
      new ClipboardItem({
        'text/html': new Blob([reformattedHtml], { type: 'text/html' }),
        'text/plain': new Blob([plainText], {
          type: 'text/plain',
        }),
      }),
    ]);
  } else {
    const converter = new Converter();
    converter.setOption('simpleLineBreaks', true);
    markdown = markdown?.replace(/</g, '&lt;');
    const htmlContent = converter.makeHtml(markdown || '');
    const plainText = resolveClipboardPlainText({
      markdown: (markdown ?? '').replace(/&lt;/g, '<'),
    });
    reportSectionMarkerResidue({
      payloads: [
        { flavor: 'html', value: htmlContent },
        { flavor: 'text', value: plainText },
      ],
      surface: 'copy',
    });
    await serializedClipboardWrite([
      new ClipboardItem({
        'text/html': new Blob([htmlContent], {
          type: 'text/html',
        }),
        'text/plain': new Blob([plainText], {
          type: 'text/plain',
        }),
      }),
    ]);
  }
};

export const oldCopyPlainText = async (copyContent: string) => {
  await writeHtmlAndMarkdownToClipboard({ markdown: copyContent });
};
